import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { VehicleChargingFigureService } from 'src/app/_services/vehicle-charging-figure.service';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-soc-end-chart',
  templateUrl: './soc-end-chart.component.html',
  styleUrls: ['./soc-end-chart.component.scss']
})
export class SocEndChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles!: any[];
  deviceSerialNumber!: any[];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  private colorMap: { [key: string]: string } = {};
  chartRange!: { range: [number, number]; };

  constructor(
    private readonly vehicleChargingFigureService: VehicleChargingFigureService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    this.vehicleChargingFigureService.getStateOfCharge(this.deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data)=>{
      this.plotSocEndChart(data);
      },
      error: (error) => {
        console.error('Error fetching State of Charge data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      },
      complete: () => {
        this.dataLoading = false;
      }
    })
  }

  plotSocEndChart(data: any): void {
    this.initializeColorMap();
    const transformXAxisPipe = new TransformXAxisPipe();
    let hasData = false;

    const plotData = Object.keys(data).flatMap((deviceSerialNumberId: string) => {
      const deviceDataArray = data[deviceSerialNumberId];
      const vehicle = this.selectedVehicles.find(v => v.batteryIdentifier === deviceSerialNumberId)
      const filteredData = deviceDataArray.filter((dataObject: any) => dataObject.chargingSessionsEnd !== null);
      const bin = filteredData.map((dataObject: any) => dataObject.binSoc);
      const socEnd = filteredData.map((dataObject: any) => dataObject.chargingSessionsEnd);

      if (socEnd.length > 0) {
        hasData = true;
      }

      this.chartRange = transformXAxisPipe.transform(bin, socEnd, 10, 100);

      return {
        x: bin,
        y: socEnd,
        type: 'bar',
        name: vehicle ? vehicle.name : deviceSerialNumberId,
        marker: {
          color: this.colorMap[deviceSerialNumberId]
        }
      };
    });

    if (!hasData) {
      this.errorMessage = true;
      return;
    } else {
      this.errorMessage = false;
    }

    const layout = {
      title: 'End State of Charge',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'End State of Charge [%]',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Charging Sessions [%]'
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('soc-end-chart', plotData, layout);
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }
}
